<template>
  <div class="entity-form">
    <v-container id="fcreate" fluid>
      <breadcrumbs />
      <v-row>
        <v-col cols="12" md="7">
          <div class="d-flex align-center justify-space-between">
            <h1 class="mb-2">
              {{ title }}
            </h1>
            <slot name="top-action" :item="item" :loading="loading"></slot>
          </div>
        </v-col>
      </v-row>

      <v-form ref="form" v-model="valid">
        <v-row>
          <v-col cols="12" md="7">
            <v-card>
              <v-card-text>
                <div
                  v-for="field in fields"
                  :key="field.key"
                  class="entity-form__row"
                >
                  <slot :name="`field.${field.key}`" :item="item">
                    <component
                      :is="field.component"
                      v-model="item[field.key]"
                      :label="field.label"
                      :create-entity="createEntity"
                      :enum-class="field.enumClass"
                      outlined
                      required
                      :rules="getFieldValidationRules(field)"
                      :loading="loading"
                      :disabled="loading"
                    />
                  </slot>
                </div>
              </v-card-text>

              <v-card-text>
                <v-row v-if="createEntity">
                  <v-col>
                    <v-btn
                      block
                      class="mb-2"
                      large
                      color="primary"
                      :loading="loading"
                      :disabled="loading"
                      @click="createNewEntity"
                    >
                      Создать
                    </v-btn>
                  </v-col>
                  <v-col>
                    <v-btn
                      block
                      large
                      color="error"
                      :loading="loading"
                      :disabled="loading"
                      @click.prevent="$router.back()"
                    >
                      Отмена
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row v-else>
                  <v-col md="4" cols="12">
                    <v-btn
                      class="mb-md-2 mb-sm-1"
                      block
                      large
                      color="primary"
                      :loading="loading"
                      :disabled="loading"
                      @click.prevent="editEntity({ redirect: true })"
                    >
                      Сохранить
                    </v-btn>
                  </v-col>
                  <v-col md="4" cols="12">
                    <v-btn
                      class="mb-md-2 mb-sm-1"
                      block
                      large
                      color="success"
                      :loading="loading"
                      :disabled="loading"
                      @click.prevent="editEntity({ redirect: false })"
                    >
                      Применить
                    </v-btn>
                  </v-col>
                  <v-col md="4" cols="12">
                    <v-btn
                      block
                      large
                      color="error"
                      :loading="loading"
                      :disabled="loading"
                      @click.prevent="goToListing"
                    >
                      Отменить
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-form>

      <v-snackbar v-model="snackbar">
        Исправьте ошибки и повторите попытку
        <template v-slot:action="{ attrs }">
          <v-btn
            color="red"
            text
            v-bind="attrs"
            @click="snackbar = false"
          >
            Закрыть
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
  import rules from '@/mixins/rules'
  import snackbarMessage from '@/mixins/snackbar-message'
  import { RestApiDataService } from '@/services/api/RestApiDataService'

  export default {
    name: 'EntityEditForm',
    mixins: [rules, snackbarMessage],
    emits: ['entity-created', 'entity-edited'],
    props: {
      title: String,
      dataService: RestApiDataService,
      fields: Array,
      itemId: {
        type: [String, Number],
        default: null,
      },

      createEntity: {
        type: Boolean,
        default: false,
      },
      listRouteName: String,
      listRouteFilter: {
        type: Object,
        default: () => ({}),
      },
    },
    data () {
      return {
        item: {},
        valid: false,
        loading: false,
        snackbar: false,
      }
    },
    computed: {
      isEditMode () {
        return !this.createEntity
      },
    },
    created () {
      this.createItemFields()

      if (this.isEditMode) {
        this.fetchEditedItemData()
      }
    },
    methods: {
      createItemFields () {
        this.item = this.fields.reduce((acc, { key, defaultValue }) => {
          return { ...acc, [key]: this.createEntity ? defaultValue : null }
        }, {})
      },
      async fetchEditedItemData () {
        this.loading = true
        try {
          const dataFromApi = await this.dataService.getOne(this.itemId)
          this.loading = false
          this.mapDataToItem(dataFromApi)
        } catch (err) {
          this.showErrorFromResponse(err)
        }
      },
      mapDataToItem (dataFromApi) {
        Object.keys(this.item).forEach((key) => {
          if (dataFromApi[key] !== undefined) {
            this.item[key] = dataFromApi[key]
          }
        })
      },
      getFieldValidationRules (field) {
        const rules = []

        if (field.rules && field.rules instanceof Array) {
          field.rules.forEach((rule) => {
            if (this.rules[rule]) {
              rules.push(this.rules[rule])
            }

            if (rule instanceof Function) {
              rules.push(rule)
            }
          })
        }

        return rules.flat()
      },

      async createNewEntity () {
        const validate = this.$refs.form.validate()

        if (!validate) {
          this.showValidationErrorMsg()
          return
        }

        this.loading = true

        try {
          await this.dataService.create(this.item)
          this.$emit('entity-created')
          this.goToListing()
        } catch (err) {
          this.showErrorFromResponse(err)
        }

        this.loading = false
      },

      async editEntity ({ redirect = false }) {
        const validate = this.$refs.form.validate()
        if (!validate) {
          this.showValidationErrorMsg()
          return
        }

        this.loading = true
        try {
          // const data = this.data

          const savedData = await this.dataService.update(this.itemId, this.item)
          this.showSuccessMessage('Изменения сохранены')
          this.$emit('entity-edited', savedData)
          if (redirect) {
            this.goToListing()
          } else {
            this.mapDataToItem(savedData)
          }
        } catch (err) {
          this.showErrorFromResponse(err)
        }
        this.loading = false
      },

      showValidationErrorMsg () {
        this.snackbar = true
        document.getElementById('fcreate').scrollIntoView({
          behavior: 'smooth',
        })
        setTimeout(() => {
          this.snackbar = false
        }, 3000)
      },
      goToListing () {
        this.$router.go(-1) // push({ name: this.listRouteName })
      },
    },
  }
</script>

<style lang="less" scoped>
.entity-form {
  &__row {
    margin-bottom: 15px;
  }
}
</style>
